import axios from "axios";
import { ListQueryParams } from "@/models/table";

// The company page's Bonds tab (TED /interface/api/bonds/, epic ortex&133). Keys arrive
// camelCased by the project-wide renderer.
//
// The app sets `axios.defaults.validateStatus = status < 500`, so a 403 (not entitled) or 404
// resolves rather than throws: the payload calls check the status themselves and throw a
// `BondsHttpError`, so a refusal can never be read as a bond. The grid fetchers hand the DataGrid
// TED's answer as it is, because the grid draws its own paywall teaser.
const BASE = "/interface/api/bonds";

export class BondsHttpError extends Error {
    constructor(public status: number) {
        super(`Bonds API answered ${status}`);
    }
}

/** True when the error is "you may not see this" rather than "this does not exist". */
export const isBondsLocked = (err: unknown) => err instanceof BondsHttpError && (err.status === 401 || err.status === 403);

const payload = <T>(res: { status: number; data: T }): T => {
    if (res.status !== 200) throw new BondsHttpError(res.status);
    return res.data;
};

export interface BondsAvailability {
    hasData: boolean;
    bonds: number;
}

export interface BondsTabPayload {
    hasData: boolean;
    summary?: {
        bonds: number;
        shownInGrid: number;
        /** Largest in dollars first; ``amountUsd`` is null for a currency with no spot rate. */
        amountByCurrency: { currency: string; amount: number; amountUsd: number | null }[];
        averageCoupon: number | null;
        nextMaturity: string | null;
        specialBonds: number;
    };
    maturityWall?: { year: number; currency: string; amount: number }[];
    events?: { date: string | null; type: string; headline: string; isin: string }[];
    ratingActions?: { date: string | null; agency: string; action: string; rating: string; isin: string; bond: string }[];
}

/** The tab probe: does this listing's company have live bonds? */
export const getBondsForStock = async (tradingitemId: number): Promise<BondsAvailability> => {
    return payload(await axios.get(`${BASE}/stock/${tradingitemId}`));
};

/** Summary, maturity wall and recent events for the tab. */
export const getBondsTab = async (tradingitemId: number, signal?: AbortSignal): Promise<BondsTabPayload> => {
    return payload(await axios.get(`${BASE}/stock/${tradingitemId}/tab`, { signal }));
};

/** The bond grid, in the platform list format (server-defined columns). */
export const getStockBonds = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url, data } = params;
    const url = next_page_url || `${BASE}/stock/bonds`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options, ...data };
    const res = await axios.get(url, { params: { ...defaultParams }, signal });
    return res.data;
};

/** A company's primary listing, for its stock page. */
export interface BondListing {
    /** Absent on grid rows, which carry only the exchange and ticker a stock link needs. */
    tradingitemId?: number;
    ticker: string;
    exchange: string;
}

/** The lending snapshot on a bond's page (TED's BondLending row, latest day). */
export interface BondLendingSnapshot {
    asOf?: string | null;
    valueOnLoanUsd?: number | string | null;
    quantityOnLoan?: number | string | null;
    loans?: number | null;
    feeAvg?: number | string | null;
    utilisationPct?: number | string | null;
    pctOfIssueOnLoan?: number | string | null;
    isSpecial?: boolean | null;
    [key: string]: number | string | boolean | null | undefined;
}

export interface BondTerm {
    field: string;
    label: string;
    value: string | number | boolean | null;
    source: string;
}

export interface BondPagePayload {
    ortexBid: number;
    name: string;
    status: string;
    issuer: {
        name: string;
        country: string;
        lei: string;
        isSovereign: boolean;
        isSubsidiary: boolean;
        companyName: string;
        listing: BondListing | null;
    };
    terms: BondTerm[];
    callSchedule: { date: string; pricePct?: number }[];
    putSchedule: { date: string; pricePct?: number }[];
    features: string[];
    venues: string[];
    centralBank: { ecbEligible: boolean; ecbHaircutCategory: string; ecbHaircutPct: number | null; csppHolder: string; csppListDate: string | null };
    ratings: { agencyName: string; rating: string; timeHorizon: string; lastAction: string; actionDate: string | null; isWithdrawn: boolean; currencyBasis: string }[];
    /**
     * TED builds these rows from `rating__agency_name`, which the camelCase renderer turns into
     * `rating_AgencyName`; `ratingAgencyName` is read too, for when TED renames it.
     */
    ratingHistory: { actionDate: string; actionType: string; actionLabel: string; ratingValue: string; ratingAgencyName?: string; rating_AgencyName?: string; rank: number | null }[];
    lending: BondLendingSnapshot | null;
    fundHolders: {
        quarter: string;
        asOf: string | null;
        funds: number;
        parHeld: number | null;
        valueUsd: number | null;
        valueOnLoanUsd: number | null;
        pctOfIssueHeld: number | null;
        reportedInDefault: boolean;
        topHolders: { fund: string; seriesId: string; par: number | null; valueUsd: number | null }[];
    }[];
    events: { announcedAt: string; eventType: string; headline: string; isThisBond: boolean }[];
    documents: { source: string; documentType: string; url: string; filedAt: string | null }[];
}

export interface BondLendingPoint {
    date: string;
    valueOnLoanUsd: number | null;
    utilisationPct: number | null;
    feeAvg: number | null;
    pctOfIssueOnLoan: number | null;
    isSpecial: boolean | null;
}

export interface BondMarketPanels {
    currency: string;
    maturityWalls: { issuer: string; country: string; amount: number; bonds: number; listing: BondListing | null }[];
    ratingChanges: { date: string; direction: "upgrade" | "downgrade" | "default"; agency: string; rating: string; ortexBid: number; bond: string; issuer: string; listing?: BondListing | null }[];
    expensiveToBorrow: {
        ortexBid: number;
        bond: string;
        issuer: string;
        currency: string;
        fee: number;
        utilisationPct: number | null;
        valueOnLoanUsd: number | null;
        isSpecial: boolean;
        asOf: string;
        pctOfIssueOnLoan?: number | null;
        listing?: BondListing | null;
    }[];
    newIssues: { date: string | null; headline: string; issuer: string; ortexBid: number | null; couponRate: string | null; maturityDate: string | null; amount: string | null; listing?: BondListing | null }[];
}

export const getBondPage = async (ortexBid: number | string, signal?: AbortSignal): Promise<BondPagePayload> => {
    return payload(await axios.get(`${BASE}/bond/${ortexBid}`, { signal }));
};

export const getBondLending = async (ortexBid: number | string, signal?: AbortSignal): Promise<{ rows: BondLendingPoint[]; available: boolean }> => {
    return payload(await axios.get(`${BASE}/bond/${ortexBid}/lending`, { signal }));
};

export const getBondMarketPanels = async (currency: string, signal?: AbortSignal): Promise<BondMarketPanels> => {
    return payload(await axios.get(`${BASE}/market/panels`, { params: { currency }, signal }));
};

/** The market grid; `data` carries the page's own filters (narrowed server-side before the grid). */
export const getBondMarket = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url, data } = params;
    const url = next_page_url || `${BASE}/market/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options, ...data };
    const res = await axios.get(url, { params: { ...defaultParams }, signal });
    return res.data;
};

/** One category's day in a FINRA TRACE segment; volumes are par in US$ millions, as FINRA publishes them. */
export interface UsBondMarketCategory {
    category: string;
    totalTrades: number | null;
    totalVolumeMm: number | null;
    advances: number | null;
    declines: number | null;
    unchanged: number | null;
    newHighs52w: number | null;
    newLows52w: number | null;
    customerBuyVolumeMm: number | null;
    customerSellVolumeMm: number | null;
    interdealerVolumeMm: number | null;
}

/**
 * US bond market activity from FINRA TRACE market aggregates (ted#2574). Open to everyone: FINRA's
 * terms allow it only free of charge and credited to FINRA, so the page shows it on the locked
 * page too and always prints `attribution`.
 */
export interface UsBondMarket {
    asOf: string | null;
    source: string;
    sourceUrl: string;
    attribution: string;
    segments: { segment: "corporate" | "corporate_144a" | "agency"; label: string; asOf: string; categories: UsBondMarketCategory[] }[];
    series: { date: string; investmentGradeVolumeMm?: number | null; highYieldVolumeMm?: number | null; advances?: number | null; declines?: number | null }[];
    treasury: {
        asOf: string;
        /** US$ billions, as FINRA publishes them. */
        products: { product: string; dealerCustomerVolumeBn: number | null; atsInterdealerVolumeBn: number | null; dealerCustomerCount: number | null; atsInterdealerCount: number | null }[];
    } | null;
}

export const getUsBondMarket = async (signal?: AbortSignal): Promise<UsBondMarket> => {
    return payload(await axios.get(`${BASE}/market/us`, { signal }));
};
